import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableContainer from "@mui/material/TableContainer";
import Typography from '@mui/material/Typography';
import { accountRowOrder, formatAccountRowLabel } from '../Utilities/accountTableRowOrder';
import { calculateYTG, calculateYTGPercentage, copyAccountTableToClipboard, isForecastInPast, isPeriodInActualsWindow, resolveCurrentPeriodIndex } from '../Utilities/tableUtils';
import { formatWeekHeader, isDatePeriod } from '../Utilities/weekNumberUtils';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import './SkuBaseForecastTable.css';

const OutputsAccountTable = ({ data, ytgSourceData = null, startMonth, selectedForecast, aggregationPeriod = "monthly", showActuals = false, loadingActuals = false, showCompare = false }) => {
    const [showDetailedUnits, setShowDetailedUnits] = useState(false);
    const [showFixedSpendDetails, setShowFixedSpendDetails] = useState(false);
    const baseMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const getOrderedMonthNames = (startMonth) => {
        const monthNames = [...baseMonthNames];
        const rotationIndex = (startMonth - 1) % 12;

        const rotatedMonths = [...monthNames.slice(rotationIndex), ...monthNames.slice(0, rotationIndex)];

        return [...rotatedMonths, 'YTG', 'FY'];
    };

    const currentYear = new Date().getFullYear();
    const currentCalendarMonth = new Date().getMonth() + 1; // 1-12
    const selectedYear = selectedForecast?.startDate ? new Date(selectedForecast.startDate).getFullYear() : currentYear;
    const totalPeriods = aggregationPeriod === "monthly" ? 12 : (data?.baseUnits?.length - 1 || 12);
    
    const showYTG = !isForecastInPast(selectedYear, currentYear, data?.currentMonth, totalPeriods, startMonth, currentCalendarMonth);

    // Use period names from API response if available, otherwise fall back to month names
    const rawPeriodNames = data?.periodNames || getOrderedMonthNames(startMonth);
    // Build period names array: always filter out YTG/FY, then add them back
    // YTG is conditionally rendered in the body, so include it in array only when showing
    let basePeriodNames = [...(rawPeriodNames.filter(name => name !== 'YTG' && name !== 'FY'))];
    if (showYTG) {
        basePeriodNames.push('YTG');
    }
    basePeriodNames.push('FY');
    
    // Format period names with week numbers if it's weekly aggregation
    const periodNames = aggregationPeriod === "weekly" 
        ? basePeriodNames.map((period, index) => {
            // Check if this looks like a date (DD-MM-YYYY or DD-MM-YY format) 
            if (/^\d{2}-\d{2}-\d{2,4}$/.test(period)) {
                return formatWeekHeader(period, index);
            }
            return period; // Keep YTG, FY, etc. as is
        })
        : basePeriodNames;

    const addCommas = (value) => {
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const formatCurrency = (value) => {
        const numericValue = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(numericValue)) {
            return '—';
        }
        let formattedValue = Math.round(numericValue * 100) / 100;
        formattedValue = formattedValue % 1 === 0 ? formattedValue.toString() : formattedValue.toFixed(2);
        return `£${addCommas(formattedValue)}`;
    };

    const formatPercentage = (value) => {
        const numericValue = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(numericValue)) {
            return '—';
        }
        let formattedValue = Math.round(numericValue * 100) / 100;
        formattedValue = formattedValue % 1 === 0 ? formattedValue.toString() : formattedValue.toFixed(2);
        return `${addCommas(formattedValue)}%`;
    };

    const formatValue = (key, value) => {
        if (value === undefined || value === null) return '—';

        if (key === 'grossProfitPercent') {
            return formatPercentage(value);
        } else if (['grossRevenue', 'overlay', 'triggers', 'fixedSpend', 'netRevenue', 'cogs', 'logs', 'grossProfit'].includes(key)) {
            return formatCurrency(value);
        } else {
            const numericValue = typeof value === 'number' ? value : Number(value);
            if (!Number.isFinite(numericValue)) {
                return '—';
            }
            return addCommas(numericValue.toLocaleString());
        }
    };

    const formatVariance = (key, value) => {
        if (value === null || value === undefined) {
            return '—';
        }
        const formatted = formatValue(key, value);
        return value > 0 ? `+${formatted}` : formatted;
    };

    const sumNullable = (values) => {
        const numericValues = values.filter((value) => typeof value === 'number' && Number.isFinite(value));

        if (numericValues.length === 0)
            return null;

        return numericValues.reduce((sum, value) => sum + value, 0);
    };

    const getActualSeriesForKey = (key, sourceData = data) => {
        if (!showActuals || !sourceData) {
            return null;
        }
        if (key === 'grossRevenue') {
            return sourceData?.actualGrossRevenue;
        }
        if (key === 'totalUnits') {
            return sourceData?.actualTotalUnits;
        }
        if (key === 'triggers') {
            return sourceData?.actualTriggers;
        }
        if (key === 'overlay') {
            return sourceData?.actualOverlay;
        }
        if (key === 'fixedSpend') {
            return sourceData?.actualFixedSpend;
        }
        if (key === 'cogs') {
            return sourceData?.actualCogs;
        }
        if (key === 'logs') {
            return sourceData?.actualLogs;
        }
        if (key === 'netRevenue') {
            return sourceData?.actualNetRevenue;
        }
        if (key === 'grossProfit') {
            return sourceData?.actualGrossProfit;
        }
        if (key === 'grossProfitPercent') {
            return sourceData?.actualGrossProfitPercent;
        }
        return null;
    };

    const getActualValue = (key, index) => {
        const series = getActualSeriesForKey(key);
        if (!Array.isArray(series)) {
            return null;
        }
        const value = series[index];
        return value === undefined ? null : value;
    };

    const getYtgDisplay = (key, forecastSeries) => {
        const ytgData = ytgSourceData || data;
        if (!ytgData) {
            return { value: null, state: 'forecast' };
        }
        const ytgPeriodNames = ytgData?.periodNames || rawPeriodNames;
        const ytgCurrentPeriod = ytgData?.currentMonth ?? data?.currentMonth;
        const ytgAggregationPeriod = ytgSourceData && aggregationPeriod !== 'weekly' ? 'weekly' : aggregationPeriod;
        const ytgForecastSeries = key === 'totalUnits'
            ? calculateTotalUnits(ytgData)
            : (ytgData?.[key] || []);
        if (!Array.isArray(ytgForecastSeries)) {
            return { value: null, state: 'forecast' };
        }
        const actualSeries = getActualSeriesForKey(key, ytgData);
        const supportsActual = key === 'grossRevenue' || key === 'totalUnits' || key === 'triggers' || key === 'overlay' || key === 'fixedSpend' || key === 'cogs' || key === 'logs' || key === 'netRevenue' || key === 'grossProfit' || key === 'grossProfitPercent';

        const value = key === 'grossProfitPercent'
            ? calculateYTGPercentage(ytgData.grossProfit, ytgData.netRevenue, ytgCurrentPeriod, selectedYear, currentYear, ytgAggregationPeriod, startMonth, currentCalendarMonth, true, ytgPeriodNames)
            : calculateYTG(ytgForecastSeries, ytgCurrentPeriod, selectedYear, currentYear, ytgAggregationPeriod, startMonth, currentCalendarMonth, true, ytgPeriodNames);

        const totalPeriods = ytgAggregationPeriod === 'monthly' ? 12 : Math.max(0, ytgForecastSeries.length - 1);
        const startIndex = resolveCurrentPeriodIndex(ytgCurrentPeriod, ytgAggregationPeriod, ytgPeriodNames);
        const boundedStartIndex = Math.max(0, Math.min(startIndex, totalPeriods));
        const range = Array.from({ length: Math.max(0, totalPeriods - boundedStartIndex) }, (_, offset) => boundedStartIndex + offset);
        const hasAnyForecastValue = range.some(idx => ytgForecastSeries[idx] !== null && ytgForecastSeries[idx] !== undefined);

        if (!hasAnyForecastValue) {
            return { value: null, state: 'forecast' };
        }

        if (!showActuals || !supportsActual || !Array.isArray(actualSeries)) {
            return { value, state: 'forecast' };
        }
        const hasAnyActual = range.some(idx => actualSeries[idx] !== null && actualSeries[idx] !== undefined);
        const hasAnyForecastFallback = range.some(idx => (actualSeries[idx] === null || actualSeries[idx] === undefined) && ytgForecastSeries[idx] !== null && ytgForecastSeries[idx] !== undefined);

        if (hasAnyActual && hasAnyForecastFallback) {
            return { value, state: ytgAggregationPeriod === 'weekly' ? 'actual' : 'mixed' };
        }
        if (hasAnyActual) {
            return { value, state: 'actual' };
        }
        return { value, state: 'forecast' };
    };

    const renderValueCell = (key, forecastValue, actualValue, periodIndex = null, stateOverride = null) => {
        const isActualWindowPeriod = periodIndex !== null && isPeriodInActualsWindow(periodIndex, data?.currentMonth);

        if (showActuals && (key === 'baseUnits' || key === 'promoUnits') && isActualWindowPeriod) {
            return (
                <Typography
                    variant="body2"
                    sx={{ color: 'primary.main', fontWeight: 700 }}
                >
                    —
                </Typography>
            );
        }

        const supportsActual = key === 'grossRevenue' || key === 'totalUnits' || key === 'triggers' || key === 'overlay' || key === 'fixedSpend' || key === 'cogs' || key === 'logs' || key === 'netRevenue' || key === 'grossProfit' || key === 'grossProfitPercent';
        if (!showActuals || !supportsActual) {
            return formatValue(key, forecastValue);
        }

        const inferredFyIndex = Array.isArray(data?.baseUnits)
            ? data.baseUnits.length - 1
            : (Array.isArray(data?.grossRevenue) ? data.grossRevenue.length - 1 : null);
        const isFy = inferredFyIndex !== null && periodIndex !== null && periodIndex === inferredFyIndex;
        const isCompletedFy = isFy && !showYTG;

        const resolveState = (hasActual) => {
            if (stateOverride) {
                return stateOverride;
            }
            if (!hasActual) {
                return ((isActualWindowPeriod && periodIndex !== null) || isCompletedFy) ? 'actual' : 'forecast';
            }
            if (periodIndex === null) {
                return aggregationPeriod === 'weekly' ? 'actual' : 'mixed';
            }
            if (aggregationPeriod === 'weekly') {
                return 'actual';
            }
            return isActualWindowPeriod ? 'actual' : 'mixed';
        };
        const shouldShowMixedTooltip = (state) => state === 'mixed';
        const styleForState = (state) => {
            if (state === 'actual') {
                return { color: 'primary.main', fontWeight: 700 };
            }
            if (state === 'mixed') {
                return {
                    color: (theme) => theme.palette.mode === 'dark' ? '#7fd5ff' : '#005a9e',
                    fontWeight: 700,
                    px: 0.5,
                    borderRadius: 0.75,
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(127, 213, 255, 0.45)' : 'rgba(0, 90, 158, 0.35)',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(127, 213, 255, 0.12)' : 'rgba(0, 90, 158, 0.1)'
                };
            }
            return { color: 'text.primary', fontWeight: 500 };
        };
        const renderStyledValue = (value, state) => {
            const content = (
                <Typography
                    variant="body2"
                    sx={{ display: 'inline-block', ...styleForState(state) }}
                >
                    {value}
                </Typography>
            );
            if (!shouldShowMixedTooltip(state)) {
                return content;
            }
            return (
                <Tooltip title="Includes both actuals and forecast values for this period." arrow>
                    <Box component="span" sx={{ display: 'inline-block' }}>
                        {content}
                    </Box>
                </Tooltip>
            );
        };

        const isLoading = loadingActuals;
        const forecastIsNumber = typeof forecastValue === 'number' && !Number.isNaN(forecastValue);
        const safeForecast = forecastIsNumber ? forecastValue : 0;
        const hasActual = actualValue !== null && actualValue !== undefined;
        const state = resolveState(hasActual);
        const variance = hasActual && forecastIsNumber ? actualValue - safeForecast : null;
        
        if (!showCompare) {
            const displayValue = hasActual ? actualValue : (isActualWindowPeriod ? null : forecastValue);
            return renderStyledValue(
                isLoading ? '…' : (displayValue !== null ? formatValue(key, displayValue) : '—'),
                state
            );
        }

        const actualDisplay = isLoading
            ? '…'
            : hasActual
                ? formatValue(key, actualValue)
                : '—';
        const varianceDisplay = isLoading
            ? '…'
            : variance !== null
                ? formatVariance(key, variance)
                : '—';
        const varianceColor = variance === null || isLoading
            ? 'text.secondary'
            : variance > 0
                ? 'success.main'
                : variance < 0
                    ? 'error.main'
                    : 'text.secondary';

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
                <Typography
                    variant="caption"
                    component="span"
                    color="text.secondary"
                    sx={{ display: 'block', whiteSpace: 'nowrap', fontSize: '0.9rem' }}
                >
                    {formatValue(key, forecastValue)}
                </Typography>
                {renderStyledValue(actualDisplay, state)}
                <Typography
                    variant="caption"
                    component="span"
                    sx={{ display: 'block', whiteSpace: 'nowrap', color: varianceColor }}
                >
                    {varianceDisplay}
                </Typography>
            </Box>
        );
    };



    const copyToClipboard = async () => {
        await copyAccountTableToClipboard(data, startMonth, selectedYear, currentYear, aggregationPeriod, showFixedSpendDetails, showActuals, ytgSourceData || undefined);
    };

    const calculateTotalUnits = (data) => {
        if (!data.baseUnits || !data.promoUnits) return [];
        return data.baseUnits.map((baseUnit, index) => {
            const promoUnit = data.promoUnits[index];
            if (baseUnit === null || baseUnit === undefined || promoUnit === null || promoUnit === undefined) {
                return null;
            }

            return (baseUnit ?? 0) + (promoUnit ?? 0);
        });
    };

    const handleToggleClick = () => {
        setShowDetailedUnits(prev => !prev);
    };

    const handleFixedSpendToggleClick = () => {
        setShowFixedSpendDetails(prev => !prev);
    };

    const renderRows = (data) => {
        const rows = [];
        const totalUnits = calculateTotalUnits(data);
        
        // Calculate number of periods (excluding FY total)
        const periodCount = (data.baseUnits || []).length - 1;
        const fyIndex = periodCount;
        const isPastPeriodIndex = (index) => isPeriodInActualsWindow(index, data?.currentMonth);
        
        // Add Total Units row
        if (totalUnits.length > 0) {
            const ytgDisplay = getYtgDisplay('totalUnits', totalUnits);
            const fyValue = totalUnits[fyIndex];
            
            rows.push(
                <TableRow key="totalUnits" sx={{ height: 40 }}>
                    <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                            height: 40, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap', 
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                        onClick={handleToggleClick}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                            Total Units
                            {showDetailedUnits ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </Box>
                    </TableCell>
                    {totalUnits.slice(0, periodCount).map((value, index) => (
                        <TableCell
                            key={index}
                            align="right"
                            sx={{
                                height: 40,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {renderValueCell('totalUnits', value, getActualValue('totalUnits', index), index)}
                        </TableCell>
                    ))}
                    {showYTG && (
                        <TableCell
                            key="ytg"
                            align="right"
                            sx={{
                                height: 40,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: 'bold'
                            }}
                        >
                            {renderValueCell('totalUnits', ytgDisplay.value, null, null, ytgDisplay.state)}
                        </TableCell>
                    )}
                    {/* FY Column */}
                    <TableCell
                        key="fy"
                        align="right"
                        sx={{
                            height: 40,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 'bold'
                        }}
                    >
                        {renderValueCell('totalUnits', fyValue, getActualValue('totalUnits', fyIndex), fyIndex)}
                    </TableCell>
                </TableRow>
            );
        }

        // Add other rows
        accountRowOrder.forEach((key) => {
            if (key !== 'overlay' && !Array.isArray(data[key])) return;

            if (!showDetailedUnits && (key === 'baseUnits' || key === 'promoUnits')) return;

            const rowSeries = data[key] || Array.from({ length: periodCount + 1 }, () => null);
            const ytgDisplay = getYtgDisplay(key, rowSeries);

            const isUnitsRow = key === 'baseUnits' || key === 'promoUnits';
            const fyValue = showActuals && isUnitsRow
                ? sumNullable(
                    rowSeries
                        .slice(0, periodCount)
                        .filter((_, index) => !isPastPeriodIndex(index))
                )
                : rowSeries[fyIndex];
            const isFixedSpendRow = key === 'fixedSpend';
            const hasPromotions = isFixedSpendRow && data.fixedSpendPromotions && data.fixedSpendPromotions.length > 0;
            const labelText = formatAccountRowLabel(key);
            
            rows.push(
                <TableRow key={key} sx={{ height: 40 }}>
                    <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                            height: 40, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            paddingLeft: isUnitsRow ? 3 : 2,
                            cursor: hasPromotions ? 'pointer' : 'default',
                            '&:hover': hasPromotions ? {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            } : {}
                        }}
                        onClick={hasPromotions ? handleFixedSpendToggleClick : undefined}
                    >
                        {hasPromotions ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                                {labelText}
                                {showFixedSpendDetails ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>
                        ) : (
                            labelText
                        )}
                    </TableCell>
                    {rowSeries.slice(0, periodCount).map((value, index) => (
                        <TableCell
                            key={index}
                            align="right"
                            sx={{
                                height: 40,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {renderValueCell(key, value, getActualValue(key, index), index)}
                        </TableCell>
                    ))}
                    {showYTG && (
                        <TableCell
                            key="ytg"
                            align="right"
                            sx={{
                                height: 40,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: 'bold'
                            }}
                        >
                            {renderValueCell(key, ytgDisplay.value, null, null, ytgDisplay.state)}
                        </TableCell>
                    )}
                    {/* FY Column */}
                    <TableCell
                        key="fy"
                        align="right"
                        sx={{
                            height: 40,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 'bold'
                        }}
                    >
                        {renderValueCell(key, fyValue, getActualValue(key, fyIndex), showActuals && isUnitsRow ? null : fyIndex)}
                    </TableCell>
                </TableRow>
            );

            if (isFixedSpendRow && showFixedSpendDetails && data.fixedSpendPromotions) {
                data.fixedSpendPromotions.forEach((promo) => {
                    const promoPeriodNames = (ytgSourceData || data)?.periodNames || rawPeriodNames;
                    const promoCurrentPeriod = (ytgSourceData || data)?.currentMonth ?? data?.currentMonth;
                    const promoYtgAggregationPeriod = ytgSourceData && aggregationPeriod !== 'weekly' ? 'weekly' : aggregationPeriod;
                    const promoYtgSource = ytgSourceData?.fixedSpendPromotions?.find(p => p.promotionId === promo.promotionId);
                    const promoYtgValues = promoYtgSource?.fixedSpendValues || promo.fixedSpendValues;
                    const promoYtgValue = calculateYTG(promoYtgValues, promoCurrentPeriod, selectedYear, currentYear, promoYtgAggregationPeriod, startMonth, currentCalendarMonth, true, promoPeriodNames);
                    const promoFyValue = promo.fixedSpendValues[fyIndex];
                    
                    rows.push(
                        <TableRow key={`promo-${promo.promotionId}`} sx={{ height: 40 }}>
                            <TableCell 
                                component="th" 
                                scope="row" 
                                sx={{ 
                                    height: 40, 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap',
                                    paddingLeft: 4,
                                    maxWidth: 200
                                }}
                                title={promo.promotionName}
                            >
                                {promo.promotionName}
                            </TableCell>
                            {promo.fixedSpendValues.slice(0, periodCount).map((value, index) => (
                                <TableCell
                                    key={index}
                                    align="right"
                                    sx={{
                                        height: 40,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {formatValue('fixedSpend', value)}
                                </TableCell>
                            ))}
                            {showYTG && (
                                <TableCell
                                    key="ytg"
                                    align="right"
                                    sx={{
                                        height: 40,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {formatValue('fixedSpend', promoYtgValue)}
                                </TableCell>
                            )}
                            {/* FY Column */}
                            <TableCell
                                key="fy"
                                align="right"
                                sx={{
                                    height: 40,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold'
                                }}
                            >
                                {formatValue('fixedSpend', promoFyValue)}
                            </TableCell>
                        </TableRow>
                    );
                });
            }
        });

        return rows;
    };


    return (
        <TableContainer 
            component={Paper} 
            sx={{ 
                height: '100%',
                overflow: 'auto',
                maxWidth: '100%',
                display: 'inline-block',
                width: 'fit-content',
                minWidth: '100%'
            }}
        >
            <Table 
                aria-label="account summary table" 
                stickyHeader
                sx={{ 
                    tableLayout: 'auto', 
                    width: 'auto',
                    '& .MuiTableBody-root .MuiTableCell-root': {
                        whiteSpace: 'nowrap'
                    },
                    '& .MuiTableHead-root .MuiTableCell-root': {
                        whiteSpace: 'nowrap'
                    },
                    '& .MuiTableHead-root .MuiTableCell-root[data-week-header="true"]': {
                        whiteSpace: 'pre'
                    }
                }}
            >
                <TableHead>
                    <TableRow sx={{ height: 40 }}>
                        <TableCell sx={{ 
                            height: 40,
                            backgroundColor: (theme) => theme.palette.background.paper,
                            zIndex: 1
                        }}>
                            <Tooltip title="Copy table to clipboard">
                                <IconButton
                                    onClick={copyToClipboard}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                        {periodNames.map((period, index) => {
                            const isWeekHeader = aggregationPeriod === "weekly" && isDatePeriod(period);
                            return (
                                <TableCell
                                    key={index}
                                    align="right"
                                    sx={{
                                        height: 40,
                                        fontWeight: index >= periodNames.length - 2 ? 'bold' : 'normal',
                                        whiteSpace: isWeekHeader ? 'pre' : 'nowrap',
                                        backgroundColor: (theme) => theme.palette.background.paper,
                                        zIndex: 1
                                    }}
                                    data-week-header={isWeekHeader}
                                >
                                    {period.toUpperCase()}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data ? renderRows(data) : (
                        <TableRow sx={{ height: 40 }}>
                            <TableCell colSpan={periodNames.length + 1} sx={{ height: 40 }}>Loading...</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default OutputsAccountTable;
